import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Edit,
  Upload,
  Download,
  AttachMoney,
  CheckCircle,
  Cancel,
  Store,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { orgDisplayName } from '../utils/orgDisplayName';
import { formatCityState } from '../utils/formatCityState';
import { VendorPricingSourceSummary } from '../components/vendorPricing/VendorPricingSourceSummary';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { format, addMonths } from 'date-fns';
import { useVendorStatus } from '../hooks/useVendorStatus';
import { DateField, NumberField, PageContainer, PageHeader } from '../components/ui';
import { useUrlTab } from '../hooks/useUrlTab';
import { useToast } from '../contexts/ToastContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { tokens } from '../theme/tokens';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { VendorRebateRequestsTab } from '../components/rebateRequests/VendorRebateRequestsTab';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { VendorStoreRunRebates } from '../components/VendorStoreRunRebates';
import { VendorScopedRebatesTab } from '../components/vendorSelfServe/VendorScopedRebatesTab';

interface RebateRecord {
  id: string;
  brand: string;
  vendorId: string;
  productModel: string;
  productSku?: string;
  rebateAmount: number;
  rebateType: string;
  startDate: Date | null;
  endDate: Date | null;
  description?: string;
  terms?: string;
  isActive: boolean;
  createdAt: Date | null;
  updatedAt: Date | null;
}

interface PotentialSubmissionStore {
  id: string;
  name: string;
  displayName: string;
  website?: string;
  address?: any;
  potentialSubmissions: number;
}

// Demo rebate records for sales demonstrations
const DEMO_REBATE_RECORDS: RebateRecord[] = [
  { id: '1', brand: 'Demo Brand', vendorId: 'demo', productModel: 'Pro Camera X1', productSku: 'PCX1-BLK', rebateAmount: 200, rebateType: 'instant', startDate: new Date('2026-01-01'), endDate: addMonths(new Date(), 2), description: 'Winter promotion - instant savings', terms: 'Valid on new purchases only. Cannot be combined with other offers.', isActive: true, createdAt: new Date('2025-12-15'), updatedAt: new Date('2026-01-01') },
  { id: '2', brand: 'Demo Brand', vendorId: 'demo', productModel: 'Pro Camera X1 Kit', productSku: 'PCX1-KIT', rebateAmount: 300, rebateType: 'instant', startDate: new Date('2026-01-01'), endDate: addMonths(new Date(), 2), description: 'Kit bundle savings', terms: 'Valid on new purchases only.', isActive: true, createdAt: new Date('2025-12-15'), updatedAt: new Date('2026-01-01') },
  { id: '3', brand: 'Demo Brand', vendorId: 'demo', productModel: 'Ultra Lens 24-70mm f/2.8', productSku: 'UL2470-28', rebateAmount: 150, rebateType: 'mail-in', startDate: new Date('2026-02-01'), endDate: addMonths(new Date(), 3), description: 'Professional lens rebate', terms: 'Submit receipt within 30 days of purchase. Allow 6-8 weeks for processing.', isActive: true, createdAt: new Date('2026-01-20'), updatedAt: new Date('2026-02-01') },
  { id: '4', brand: 'Demo Brand', vendorId: 'demo', productModel: 'Ultra Lens 70-200mm f/2.8', productSku: 'UL70200-28', rebateAmount: 200, rebateType: 'mail-in', startDate: new Date('2026-02-01'), endDate: addMonths(new Date(), 3), description: 'Telephoto lens rebate', terms: 'Submit receipt within 30 days of purchase. Allow 6-8 weeks for processing.', isActive: true, createdAt: new Date('2026-01-20'), updatedAt: new Date('2026-02-01') },
  { id: '5', brand: 'Demo Brand', vendorId: 'demo', productModel: 'Speedlight Pro 600', productSku: 'SP600', rebateAmount: 50, rebateType: 'instant', startDate: new Date('2026-01-15'), endDate: addMonths(new Date(), 1), description: 'Flash accessory promotion', terms: 'Valid on new purchases only.', isActive: true, createdAt: new Date('2026-01-10'), updatedAt: new Date('2026-01-15') },
  { id: '6', brand: 'Demo Brand', vendorId: 'demo', productModel: 'Entry Camera E100', productSku: 'ECE100', rebateAmount: 100, rebateType: 'instant', startDate: new Date('2026-01-01'), endDate: addMonths(new Date(), 4), description: 'Entry-level camera promotion', terms: 'Perfect for new photographers. Valid on new purchases.', isActive: true, createdAt: new Date('2025-12-20'), updatedAt: new Date('2026-01-01') },
  { id: '7', brand: 'Demo Brand', vendorId: 'demo', productModel: 'Entry Camera E100 Kit', productSku: 'ECE100-KIT', rebateAmount: 150, rebateType: 'instant', startDate: new Date('2026-01-01'), endDate: addMonths(new Date(), 4), description: 'Entry-level kit bundle savings', terms: 'Includes 18-55mm lens. Valid on new purchases.', isActive: true, createdAt: new Date('2025-12-20'), updatedAt: new Date('2026-01-01') },
  { id: '8', brand: 'Demo Brand', vendorId: 'demo', productModel: 'Battery Grip BG-X1', productSku: 'BGX1', rebateAmount: 30, rebateType: 'instant', startDate: new Date('2026-02-01'), endDate: addMonths(new Date(), 2), description: 'Accessory bundle deal', terms: 'When purchased with Pro Camera X1.', isActive: true, createdAt: new Date('2026-01-25'), updatedAt: new Date('2026-02-01') },
  { id: '9', brand: 'Demo Brand', vendorId: 'demo', productModel: 'Ultra Lens 50mm f/1.4', productSku: 'UL50-14', rebateAmount: 75, rebateType: 'trade-in', startDate: new Date('2026-01-01'), endDate: addMonths(new Date(), 6), description: 'Trade-in upgrade program', terms: 'Trade in any 50mm lens and receive credit toward new purchase.', isActive: true, createdAt: new Date('2025-12-01'), updatedAt: new Date('2026-01-01') },
  { id: '10', brand: 'Demo Brand', vendorId: 'demo', productModel: 'Pro Camera X1', productSku: 'PCX1-BLK', rebateAmount: 500, rebateType: 'trade-in', startDate: new Date('2026-01-01'), endDate: addMonths(new Date(), 6), description: 'Camera body trade-in program', terms: 'Trade in any full-frame camera body. Value varies by condition.', isActive: true, createdAt: new Date('2025-12-01'), updatedAt: new Date('2026-01-01') },
];

// Demo potential submission stores
const DEMO_SUBMISSION_STORES: PotentialSubmissionStore[] = [
  { id: '1', name: "Mike's Camera", displayName: "Mike's Camera - Denver", website: 'https://mikescamera.com', potentialSubmissions: 24 },
  { id: '2', name: 'Cardinal Camera', displayName: 'Cardinal Camera - Indianapolis', website: 'https://cardinalcamera.com', potentialSubmissions: 18 },
  { id: '3', name: 'B&H Photo', displayName: 'B&H Photo Video', website: 'https://bhphotovideo.com', potentialSubmissions: 156 },
  { id: '4', name: 'Adorama', displayName: 'Adorama Camera', website: 'https://adorama.com', potentialSubmissions: 89 },
  { id: '5', name: 'Camera West', displayName: 'Camera West - San Francisco', website: 'https://camerawest.com', potentialSubmissions: 12 },
];

const mobileLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  display: 'block',
};

export const VendorBrandRebateManagementPage: React.FC = () => {
  const { userProfile } = useAuth();
  const {
    getEffectiveUserType,
    isImpersonating,
    impersonatedRole,
    impersonatedBrand,
    impersonatedOrg,
  } = useImpersonation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const theme = useTheme();
  const dialogFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useAppTranslation();
  const { enabled: eventRequestsEnabled } = useOrgFeatureFlag('event_rebate_requests');
  const { enabled: selfServeEnabled } = useOrgFeatureFlag('vendor_self_serve');
  // Flag store_attested_rebates: a read-only tab of rebates stores run on this vendor's brands.
  const { enabled: storeRunEnabled } = useOrgFeatureFlag('store_attested_rebates');
  const [urlTab, setActiveTab] = useUrlTab(['programs', 'submissions', 'requests', 'scoped', 'store-run'] as const, 'programs');
  const activeTab = urlTab === 'store-run' && !storeRunEnabled ? 'programs' : urlTab;
  const [openDialog, setOpenDialog] = useState(false);
  const [openSubmissionsDialog, setOpenSubmissionsDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState<RebateRecord | null>(null);
  const [selectedRebateId, setSelectedRebateId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    productModel: '',
    productSku: '',
    rebateAmount: '',
    rebateType: 'instant',
    startDate: '',
    endDate: '',
    description: '',
    terms: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { showToast } = useToast();

  const effectiveUserType = getEffectiveUserType(userProfile);
  const { organization, isPendingApproval: rawIsPendingApproval, isRejected: rawIsRejected, isLoading: statusLoading } = useVendorStatus();

  // Demo mode: admin impersonating vendor without a specific brand/org
  const isDemoMode =
    isImpersonating &&
    impersonatedRole === 'vendor' &&
    !impersonatedBrand?.id &&
    !impersonatedOrg?.id &&
    !organization?.id;

  // In demo mode, override approval/status flags
  const isPendingApproval = isDemoMode ? false : rawIsPendingApproval;
  const isRejected = isDemoMode ? false : rawIsRejected;

  // Fetch rebate records (only if approved and not in demo mode)
  const { data: rebatesData, isLoading } = useQuery({
    queryKey: ['vendor-rebates'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/vendor/rebates', { headers });
      if (!response.ok) {
        throw new Error('Failed to fetch rebate records');
      }
      return response.json();
    },
    enabled: !isDemoMode && !isPendingApproval && !isRejected && effectiveUserType === 'vendor_supplier',
  });

  // Fetch potential submission stores for selected rebate (must be before early returns)
  const { data: submissionsData, isLoading: submissionsLoading } = useQuery({
    queryKey: ['vendor-rebate-submissions', selectedRebateId],
    queryFn: async () => {
      if (!selectedRebateId) return null;
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/vendor/rebates/${selectedRebateId}/submissions`, { headers });
      if (!response.ok) {
        throw new Error('Failed to fetch potential submission stores');
      }
      return response.json();
    },
    enabled: !!selectedRebateId && openSubmissionsDialog,
  });

  // Create/Update mutation (must be before early returns)
  const createUpdateMutation = useMutation({
    mutationFn: async (data: any) => {
      const headers = await getAuthHeaders();
      const url = editingRecord
        ? `/vendor/rebates/${editingRecord.id}`
        : '/vendor/rebates';
      const method = editingRecord ? 'PUT' : 'POST';
      
      const response = await makeApiCall(url, {
        method,
        headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save rebate');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-rebates'] });
      setOpenDialog(false);
      setEditingRecord(null);
      setFormData({
        productModel: '',
        productSku: '',
        rebateAmount: '',
        rebateType: 'instant',
        startDate: '',
        endDate: '',
        description: '',
        terms: '',
      });
      setError(null);
    },
    onError: (error: any) => {
      setError(error.message || 'Failed to save rebate');
    },
  });

  // Use demo data when in demo mode
  const rebateRecords: RebateRecord[] = isDemoMode ? DEMO_REBATE_RECORDS : (rebatesData?.data || []);
  const potentialStores: PotentialSubmissionStore[] = isDemoMode ? DEMO_SUBMISSION_STORES : (submissionsData?.data || []);

  // The backend has no limit/offset support for this list, so we paginate client-side.
  const paginatedRebateRecords = rebateRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Check access permissions
  if (effectiveUserType !== 'vendor_supplier') {
    return (
      <PageContainer>
        <Typography variant="h4" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1">
          This page is only accessible to vendor and supplier accounts.
        </Typography>
      </PageContainer>
    );
  }

  // Account lockout for rejected vendors
  if (isRejected) {
    return (
      <PageContainer>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Account Locked
          </Typography>
          <Typography variant="body1">
            Your vendor application has been rejected. Your account access has been suspended.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            If you believe this is an error, please contact support.
          </Typography>
        </Alert>
      </PageContainer>
    );
  }

  const handleOpenDialog = (record?: RebateRecord) => {
    if (record) {
      setEditingRecord(record);
      setFormData({
        productModel: record.productModel,
        productSku: record.productSku || '',
        rebateAmount: record.rebateAmount.toString(),
        rebateType: record.rebateType,
        startDate: record.startDate
          ? format(new Date(record.startDate), 'yyyy-MM-dd')
          : '',
        endDate: record.endDate
          ? format(new Date(record.endDate), 'yyyy-MM-dd')
          : '',
        description: record.description || '',
        terms: record.terms || '',
      });
    } else {
      setEditingRecord(null);
      setFormData({
        productModel: '',
        productSku: '',
        rebateAmount: '',
        rebateType: 'instant',
        startDate: '',
        endDate: '',
        description: '',
        terms: '',
      });
    }
    setError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRecord(null);
    setError(null);
  };

  const handleViewSubmissions = (rebateId: string) => {
    setSelectedRebateId(rebateId);
    setOpenSubmissionsDialog(true);
  };

  const handleCloseSubmissionsDialog = () => {
    setOpenSubmissionsDialog(false);
    setSelectedRebateId(null);
  };

  const handleSubmit = () => {
    if (!formData.productModel || !formData.rebateAmount || !formData.startDate) {
      setError('Product model, rebate amount, and start date are required');
      return;
    }

    const submitData: any = {
      productModel: formData.productModel,
      rebateAmount: formData.rebateAmount,
      rebateType: formData.rebateType,
      startDate: formData.startDate,
    };

    if (formData.productSku) submitData.productSku = formData.productSku;
    if (formData.endDate) submitData.endDate = formData.endDate;
    if (formData.description) submitData.description = formData.description;
    if (formData.terms) submitData.terms = formData.terms;

    createUpdateMutation.mutate(submitData);
  };

  const handleFileUpload = () => {
    // TODO: Implement CSV/Excel upload for bulk rebate creation
    showToast('Bulk upload functionality coming soon', 'info');
  };

  const handleExport = () => {
    // TODO: Implement CSV export
    showToast('Export functionality coming soon', 'info');
  };

  return (
    <PageContainer>
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            Demo Mode
          </Typography>
          <Typography variant="body2">
            This is a preview of the Rebate Management features with sample data. In production, vendors can create and manage rebate programs, and track store participation.
          </Typography>
        </Alert>
      )}

      {/* Application Under Review Banner */}
      {isPendingApproval && !isDemoMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            Application Under Review
          </Typography>
          <Typography variant="body2">
            Your vendor application is being reviewed. You can preview this page, but actions are disabled until your application is approved.
          </Typography>
        </Alert>
      )}

      <PageHeader
        title="Rebate Management"
        description="Manage rebate programs for your products and view which stores are potentially submitting rebates."
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={handleFileUpload}
              disabled={isPendingApproval}
            >
              Upload
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={isPendingApproval}
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              disabled={isPendingApproval}
            >
              Add Rebate
            </Button>
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Rebate Programs" value="programs" />
          <Tab label="Potential Submissions" value="submissions" />
          {eventRequestsEnabled && <Tab label={t('vendor.rebateRequests.tab')} value="requests" />}
          {selfServeEnabled && <Tab label={t('vendor.scopedRebates.tab')} value="scoped" />}
          {storeRunEnabled && <Tab label={t('store.attestedRebates.vendorTab')} value="store-run" />}
        </Tabs>
      </Paper>

      {activeTab === 'programs' && !isDemoMode && !isPendingApproval && !isRejected && effectiveUserType === 'vendor_supplier' && (
        <VendorPricingSourceSummary kind="rebate" enabled />
      )}

      {activeTab === 'programs' && (
        <Card>
          <CardContent>
            {!isDemoMode && statusLoading ? (
              <Typography>Loading...</Typography>
            ) : !isDemoMode && isLoading ? (
              <Typography>Loading rebate records...</Typography>
            ) : isPendingApproval ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AttachMoney sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Rebate Management Preview
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Once your vendor application is approved, you'll be able to manage rebate programs for your products here.
                  You'll also be able to see which stores are potentially submitting rebates for each program.
                </Typography>
              </Box>
            ) : rebateRecords.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AttachMoney sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Rebate Programs
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add your first rebate program to get started.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog()}
                  disabled={isPendingApproval}
                >
                  Add Rebate
                </Button>
              </Box>
            ) : isMobile ? (
              <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <Stack divider={<Divider />}>
                  {paginatedRebateRecords.map((record) => (
                    <Box key={record.id} sx={{ p: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2">{record.productModel}</Typography>
                          <Typography variant="caption" color="text.secondary">{record.productSku || '—'}</Typography>
                        </Box>
                        <Chip
                          label={record.isActive ? 'Active' : 'Inactive'}
                          color={record.isActive ? 'success' : 'default'}
                          size="small"
                          icon={record.isActive ? <CheckCircle /> : <Cancel />}
                          sx={{ flexShrink: 0 }}
                        />
                      </Stack>
                      <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
                        <Box>
                          <Typography component="span" sx={mobileLabelSx}>Rebate</Typography>
                          <Typography variant="body2">${record.rebateAmount.toFixed(2)}</Typography>
                        </Box>
                        <Box>
                          <Typography component="span" sx={mobileLabelSx}>Type</Typography>
                          <Chip label={record.rebateType} size="small" variant="outlined" />
                        </Box>
                        <Box>
                          <Typography component="span" sx={mobileLabelSx}>Window</Typography>
                          <Typography variant="body2">
                            {record.startDate ? format(new Date(record.startDate), 'MMM dd, yyyy') : '—'}
                            {' – '}
                            {record.endDate ? format(new Date(record.endDate), 'MMM dd, yyyy') : 'No end'}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ mt: 1 }}>
                        <Tooltip title="View Potential Submissions">
                          <IconButton
                            size="small"
                            onClick={() => handleViewSubmissions(record.id)}
                            disabled={isPendingApproval}
                          >
                            <Store />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(record)}
                            disabled={isPendingApproval}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product Model</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Rebate Amount</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRebateRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>{record.productModel}</TableCell>
                        <TableCell>{record.productSku || '—'}</TableCell>
                        <TableCell>${record.rebateAmount.toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={record.rebateType}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {record.startDate
                            ? format(new Date(record.startDate), 'MMM dd, yyyy')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {record.endDate
                            ? format(new Date(record.endDate), 'MMM dd, yyyy')
                            : 'No end date'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={record.isActive ? 'Active' : 'Inactive'}
                            color={record.isActive ? 'success' : 'default'}
                            size="small"
                            icon={record.isActive ? <CheckCircle /> : <Cancel />}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Potential Submissions">
                            <IconButton
                              size="small"
                              onClick={() => handleViewSubmissions(record.id)}
                              disabled={isPendingApproval}
                            >
                              <Store />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(record)}
                              disabled={isPendingApproval}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {!isPendingApproval && rebateRecords.length > 0 && (
              <TablePagination
                component="div"
                count={rebateRecords.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
              />
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'requests' && eventRequestsEnabled && <VendorRebateRequestsTab />}
      {activeTab === 'scoped' && selfServeEnabled && <VendorScopedRebatesTab />}

      {activeTab === 'store-run' && storeRunEnabled && (
        <Card>
          <CardContent>
            <VendorStoreRunRebates />
          </CardContent>
        </Card>
      )}

      {activeTab === 'submissions' && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Potential Rebate Submissions by Store
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              View which stores are potentially submitting rebates for your rebate programs. Click on a rebate in the table above to see potential submissions.
            </Typography>
            <Alert severity="info">
              Select a rebate program from the "Rebate Programs" tab and click the store icon to view potential submissions.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={dialogFullScreen}>
        <DialogTitle>
          {editingRecord ? 'Edit Rebate Program' : 'Add Rebate Program'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Product Model"
              required
              fullWidth
              value={formData.productModel}
              onChange={(e) => setFormData({ ...formData, productModel: e.target.value })}
              disabled={!!editingRecord}
              helperText={editingRecord ? 'Model cannot be changed' : 'Enter the product model name'}
            />
            <TextField
              label="SKU (Optional)"
              fullWidth
              value={formData.productSku}
              onChange={(e) => setFormData({ ...formData, productSku: e.target.value })}
            />
            <NumberField
              label="Rebate Amount"
              adornment="$"
              min={0}
              step="0.01"
              value={formData.rebateAmount}
              onChange={(v) => setFormData({ ...formData, rebateAmount: v })}
              sx={{ width: '100%' }}
            />
            <FormControl fullWidth>
              <InputLabel>Rebate Type</InputLabel>
              <Select
                value={formData.rebateType}
                label="Rebate Type"
                onChange={(e) => setFormData({ ...formData, rebateType: e.target.value })}
              >
                <MenuItem value="instant">Instant</MenuItem>
                <MenuItem value="mail_in">Mail-In</MenuItem>
                <MenuItem value="instant_mail_in">Instant or Mail-In</MenuItem>
              </Select>
            </FormControl>
            <DateField
              label="Start Date"
              value={formData.startDate}
              onChange={(v) => setFormData({ ...formData, startDate: v })}
              sx={{ width: '100%' }}
            />
            <DateField
              label="End Date (Optional)"
              value={formData.endDate}
              min={formData.startDate || undefined}
              onChange={(v) => setFormData({ ...formData, endDate: v })}
              sx={{ width: '100%' }}
            />
            <TextField
              label="Description (Optional)"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <TextField
              label="Terms & Conditions (Optional)"
              fullWidth
              multiline
              rows={3}
              value={formData.terms}
              onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
              helperText="Enter rebate terms and conditions"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createUpdateMutation.isPending}
          >
            {createUpdateMutation.isPending ? 'Saving...' : editingRecord ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Potential Submissions Dialog */}
      <Dialog
        open={openSubmissionsDialog}
        onClose={handleCloseSubmissionsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Potential Rebate Submission Stores
        </DialogTitle>
        <DialogContent>
          {submissionsLoading ? (
            <Typography>Loading stores...</Typography>
          ) : potentialStores.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Store sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Potential Submission Stores
              </Typography>
              <Typography variant="body2" color="text.secondary">
                There are currently no stores syncing products that match this rebate program.
              </Typography>
            </Box>
          ) : (
            <List>
              {potentialStores.map((store) => (
                <ListItem key={store.id}>
                  <ListItemIcon>
                    <Store color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={orgDisplayName(store)}
                    secondary={
                      <Box>
                        {store.website && (
                          <Typography variant="caption" display="block">
                            {store.website}
                          </Typography>
                        )}
                        {store.address && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {formatCityState(store.address.city, store.address.state)}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Potential submissions: {store.potentialSubmissions}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmissionsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

